/**
 * The week as the owner's own sheet draws it: employees down the side, days across the top, and
 * the shift window in every cell except the one that says Week off.
 *
 * It replaces a board of seven day columns with chips in them. That board answered "who is off on
 * Thursday", which is the question a cap is about — but the question the office actually asks, and
 * the one the hand-kept sheet was built to answer, is "when is Ravi in this week". A row per
 * person answers it at a glance; the old board needed seven columns read sideways.
 *
 * A day that has come shows what it was — Present, Absent, Week off — and a day to come shows the
 * shift hours. Clicking a cell asks what the day should be: making it the off day goes through
 * the same route the old board used (caps checked, a suggested swap, Move Anyway still the
 * admin's to press); Present or Absent is a day correction, with its reason.
 */
import { Link } from 'react-router-dom';
import {
  SHIFTS,
  SHIFT_LABEL,
  addDays,
  fmtDayMonth,
  fmtWeekday,
  shiftWindowLabel,
  windowFor,
  type EmploymentType,
  type Shift,
  type ShiftTimes,
} from '@siva/shared';
import { t } from '../i18n/index.js';
import { Avatar } from '../lib/components.js';
import { usePhotos } from './photos.js';

export type RosterWorker = {
  id: string;
  name: string;
  employment_type: EmploymentType | null;
  shift: Shift;
  designation?: string | null;
};

export type RosterWeek = {
  week_start: string;
  /** What a day may hold, per shift. Shown at the foot so a full day is seen before it refuses. */
  caps: { total: number; full_time: number; part_time: number };
  shift_times: ShiftTimes;
  days: { weekday: number; date: string; workers: RosterWorker[] }[];
  unassigned: RosterWorker[];
  no_off_day: RosterWorker[];
};

/**
 * What a day that has come actually was, from attendance: the sheet shows it in place of the
 * shift hours (the owner, 21 October 2026: "I need to see present and absent as well").
 */
export type DayRecord = {
  status: 'present' | 'present_no_punchout' | 'absent' | 'weekly_off' | 'holiday';
  adjusted: boolean;
};
/** worker id → date → the record. Days still to come have none. */
export type WeekRecords = Record<string, Record<string, DayRecord | null>>;

/** One person, their off day if they have one, and what the sheet says beside their name. */
type Row = RosterWorker & { offDate: string | null; noOff: boolean };

const rowsFor = (week: RosterWeek, shift: Shift): Row[] => {
  const rows: Row[] = [];
  for (const d of week.days) {
    for (const w of d.workers)
      if (w.shift === shift) rows.push({ ...w, offDate: d.date, noOff: false });
  }
  for (const w of week.unassigned)
    if (w.shift === shift) rows.push({ ...w, offDate: null, noOff: false });
  for (const w of week.no_off_day)
    if (w.shift === shift) rows.push({ ...w, offDate: null, noOff: true });
  return rows.sort((a, b) => a.name.localeCompare(b.name));
};

/** How a recorded day reads on the sheet. A day with no punch-out was still a day present. */
const RECORD_KIND: Record<DayRecord['status'], 'present' | 'absent' | 'off' | 'holiday'> = {
  present: 'present',
  present_no_punchout: 'present',
  absent: 'absent',
  weekly_off: 'off',
  holiday: 'holiday',
};

/** "SSA FT" — the designation and the type, the way the sheet writes them. */
const designationOf = (w: RosterWorker): string =>
  [w.designation, w.employment_type === 'part_time' ? 'PT' : 'FT'].filter(Boolean).join(' ');

export function RosterGrid({
  week,
  busy,
  readOnly = false,
  records = {},
  today,
  onCell,
  onNoOff,
}: {
  week: RosterWeek;
  busy: boolean;
  records?: WeekRecords;
  /** The work date now, so today's not-yet-in reads as hours rather than Absent. */
  today: string;
  /**
   * A week that has already finished. The row action (no off day this week) is gone; a cell
   * still opens the day, where a change is a correction with a reason.
   */
  readOnly?: boolean;
  /** A cell was pressed: the page asks what this day should be. */
  onCell: (worker: RosterWorker, weekday: number, date: string, record: DayRecord | null) => void;
  /** They work all seven days this week, or they do not. */
  onNoOff: (worker: RosterWorker, none: boolean) => void;
}) {
  const days = Array.from({ length: 7 }, (_, i) => ({
    weekday: i + 1,
    date: addDays(week.week_start, i),
  }));
  return (
    <div className={`roster${readOnly ? ' is-readonly' : ''}`}>
      {SHIFTS.map((shift) => {
        const rows = rowsFor(week, shift);
        if (rows.length === 0) return null;
        return (
          <section key={shift} className="roster-shift">
            <h4>{t('roster_shift_heading', { shift: SHIFT_LABEL[shift] })}</h4>
            <div className="table-wrap">
              <table className="roster-table">
                <thead>
                  <tr>
                    <th scope="col">{t('worker_name')}</th>
                    <th scope="col">{t('worker_designation')}</th>
                    {days.map((d) => (
                      <th key={d.date} scope="col" className="roster-day">
                        <span className="roster-weekday">{fmtWeekday(d.date).slice(0, 3)}</span>
                        <span className="muted">{fmtDayMonth(d.date)}</span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((w) => (
                    <RosterRow
                      key={w.id}
                      row={w}
                      days={days}
                      times={week.shift_times}
                      shift={shift}
                      busy={busy}
                      readOnly={readOnly}
                      records={records[w.id] ?? {}}
                      today={today}
                      onCell={onCell}
                      onNoOff={onNoOff}
                    />
                  ))}
                </tbody>
                {/*
                  How full each day is, for this shift. The caps are counted per shift and they
                  refuse a move when they are reached — without this the first anybody knows of a
                  full Thursday is the refusal itself.
                */}
                <tfoot>
                  <tr>
                    <th scope="row" colSpan={2}>
                      {t('roster_off_that_day')}
                    </th>
                    {days.map((d) => {
                      const on = rows.filter((w) => w.offDate === d.date);
                      const ft = on.filter((w) => w.employment_type !== 'part_time').length;
                      const pt = on.length - ft;
                      // The total is what fills a day; the two beneath it are what usually
                      // refuses a move first, and without them a refusal has no visible cause.
                      const atCap =
                        on.length >= week.caps.total ||
                        ft >= week.caps.full_time ||
                        pt >= week.caps.part_time;
                      return (
                        <td key={d.date} className={`roster-day${atCap ? ' at-cap' : ''}`}>
                          <span className="roster-total">
                            {on.length}/{week.caps.total}
                          </span>
                          <span className="roster-split">
                            FT {ft}/{week.caps.full_time} · PT {pt}/{week.caps.part_time}
                          </span>
                        </td>
                      );
                    })}
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          </section>
        );
      })}
    </div>
  );
}

function RosterRow({
  row,
  days,
  times,
  shift,
  busy,
  readOnly,
  records,
  today,
  onCell,
  onNoOff,
}: {
  row: Row;
  days: { weekday: number; date: string }[];
  times: ShiftTimes;
  shift: Shift;
  busy: boolean;
  records: Record<string, DayRecord | null>;
  today: string;
  onCell: (worker: RosterWorker, weekday: number, date: string, record: DayRecord | null) => void;
  readOnly: boolean;
  onNoOff: (worker: RosterWorker, none: boolean) => void;
}) {
  const { photoOf } = usePhotos();
  const hours = shiftWindowLabel(windowFor(times, row.employment_type, shift));
  return (
    <tr className={row.noOff ? 'no-off' : undefined}>
      <th scope="row" className="roster-name">
        <span className="person">
          <Avatar name={row.name} size={26} photoUrl={photoOf(row.id)} />
          <Link to={`/workers/${row.id}`}>{row.name}</Link>
        </span>
      </th>
      <td className="roster-designation">{designationOf(row)}</td>
      {days.map((d) => {
        const rec = d.date <= today ? (records[d.date] ?? null) : null;
        // A day that has come shows what it was. Today, somebody not in yet is not absent yet:
        // the hours stay until the day closes or an admin says otherwise.
        const notYet = rec?.status === 'absent' && d.date === today && !rec.adjusted;
        const kind = !rec || notYet ? null : RECORD_KIND[rec.status];
        const off = kind ? kind === 'off' : d.date === row.offDate;
        const label = kind ? t(`roster_rec_${kind}`) : off ? t('roster_week_off') : hours;
        // A day to come that is already the off day has nothing else it could be.
        const fixed = off && d.date > today;
        return (
          <td
            key={d.date}
            className={`roster-day${off ? ' is-off' : ''}${kind && kind !== 'off' ? ` is-${kind}` : ''}${rec?.adjusted ? ' is-adjusted' : ''}`}
          >
            {/*
              The cell is the control. It opens a small question — Weekly off, Present or
              Absent — rather than doing anything on the press (21 October 2026).
            */}
            <button
              type="button"
              className="roster-cell"
              disabled={busy || fixed}
              onClick={() => onCell(row, d.weekday, d.date, rec)}
              title={
                fixed ? t('roster_is_off') : t('roster_change_day', { day: fmtDayMonth(d.date) })
              }
            >
              {label}
              {rec?.adjusted && <span className="roster-adj" aria-label={t('roster_adjusted')} />}
            </button>
          </td>
        );
      })}
      <td className="roster-actions">
        {/* Somebody who works all seven days is a decision, and it is reversible from here. */}
        {!readOnly && (
          <button
            type="button"
            className="link small"
            disabled={busy}
            onClick={() => onNoOff(row, !row.noOff)}
          >
            {row.noOff ? t('roster_give_off') : t('roster_no_off')}
          </button>
        )}
      </td>
    </tr>
  );
}
